// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import ArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { Button, Paper } from '@mui/material'
import get from 'lodash/get'
import groupBy from 'lodash/groupBy'
import orderBy from 'lodash/orderBy'
import moment from 'moment'
import React from 'react'
import { Query } from 'react-apollo'
import { getConnectionNodes } from '../../../common/data/helper'
import DateBar from '../../../common/date/DateBar'
import type { WithDateProps } from '../../../common/date/withDate'
import withDate from '../../../common/date/withDate'
import EditButton from '../../../common/iconButtons/EditButton'
import Loader from '../../../common/loader/Loader'
import { getCurrentWorkingDay } from '../../../common/utils'
import Checklist from '../../checklist/Checklist'
import { fetchDailyManagement } from '../../dailyManagement/dailyManagementQueries'
import { i18n } from '../../index'
import type { Station } from '../../station/stationTypes'
import withActiveStation from '../../station/withActiveStation'
import type { Assignment } from '../../taskList/data.js'
import Counters from './Counters'
import css from './IncentivesPanel.css'
import NoteDialog from './NoteDialog'
import SalesManagement from './SalesManagement'
import StaffMemberSelect from './StaffMembersSelect'
import { getStaffingPlanWithAdditionalInfo } from './incentivesPanelQueries'
import type { CurrentUser, StaffMember } from './types'

const UNITS = {
  Pieces: 'kpl',
  Euro: '€',
}

type State = {
  currentEmployeeId: string
  currentUser: CurrentUser
  salesIncentiveUnit?: string
}

type Props = WithDateProps & {
  activeStation: Station
}

type StaffingPlan = {
  assignments: Array<Assignment>
  employeeShifts: Array<StaffMember>
}

type HashLink = {
  label: string
  hash: string
}

const defaultCurrentUser = {
  salesIncentiveTarget: 0,
  thumbsUpTarget: 0,
  thumbsUpActual: 0,
  salesIncentiveActual: 0,
  note: '',
}

const dateFormat = 'YYYY-MM-DD'

@withDate()
@withActiveStation
class IncentivesPanel extends React.Component<Props, State> {
  state = {
    isDialogOpen: false,
    currentEmployeeId: '',
    salesIncentiveUnit: null,
    currentUser: defaultCurrentUser,
  }
  openDialog = () => {
    this.setState({ isDialogOpen: true })
  }

  closeDialog = () => {
    this.setState({ isDialogOpen: false, selectedNote: null })
  }

  employeeChanged = (userId: string, employees: Array<StaffMember>) => {
    const currentUser = employees.find((employee) => employee.user.id === userId) || employees[0]

    this.setState({
      currentEmployeeId: userId,
      currentUser,
    })
  }

  setStationUnits = (salesIncentiveUnit: string) => {
    this.setState({ salesIncentiveUnit })
  }

  groupStaffPlanByEmployee = (data: StaffingPlan) => {
    const { assignments, employeeShifts } = data
    const updatedStaffPlan = []

    if (employeeShifts.length) {
      const groupedActivityByEmployee = groupBy(employeeShifts, 'user.id')

      Object.keys(groupedActivityByEmployee).forEach((groupKey) => {
        const group = groupedActivityByEmployee[groupKey]
        if (group.length > 1) {
          const orderedGroup = orderBy(group, 'startTime', 'asc')
          const firstActivity = {
            ...orderedGroup[0],
            employeeCardAdditional: {
              activities: orderedGroup.map((a) => a.position),
              responsibilities: orderedGroup.reduce((a, c) => a.concat(c.responsibility), []),
              hasActiveTask: this.checkActiveTask(orderedGroup[0].user.id, assignments),
            },
          }
          orderedGroup.forEach((employee, idx) => {
            if (idx > 0) {
              firstActivity.salesIncentiveTarget += employee.salesIncentiveTarget
              firstActivity.thumbsUpTarget += employee.thumbsUpTarget
              firstActivity.salesIncentiveActual += employee.salesIncentiveActual
              firstActivity.thumbsUpActual += employee.thumbsUpActual
            }
          })
          updatedStaffPlan.push(firstActivity)
        } else {
          updatedStaffPlan.push({
            ...group[0],
            employeeCardAdditional: {
              activities: [group[0].position],
              responsibilities: group[0].responsibility,
              hasActiveTask: this.checkActiveTask(group[0].user.id, assignments),
            },
          })
        }
      })
    }

    return updatedStaffPlan
  }

  checkActiveTask = (employeeId: string, assignments: Array<Assignment>) => {
    return (
      assignments.filter((a) => {
        return (
          (a.assignee && a.assignee.id === employeeId) ||
          (a.assignees && a.assignees.some((assignee) => assignee?.id === employeeId))
        )
      }).length > 0
    )
  }

  showSalesManagementBasedOnTime = (chosenDate: string) => {
    let show = false
    const today = moment().format(dateFormat)
    const yesterday = moment().subtract(1, 'days').format(dateFormat)
    if (
      moment(chosenDate).isSame(today) ||
      (moment(chosenDate).isSame(yesterday) && moment().subtract(7, 'hours').format(dateFormat) === yesterday)
    ) {
      show = true
    }
    return show
  }

  showYesterdayCardBeforeEndOfNightShift = (chosenDate: string) => {
    let yesterday = false
    const today = moment().format(dateFormat)

    if (moment(chosenDate).isSame(today)) {
      const currentWorkingDay = getCurrentWorkingDay()
      yesterday = moment(today).isAfter(currentWorkingDay) && currentWorkingDay
    }
    return yesterday
  }

  onNavigationClick = (id: string) => {
    const element = document.getElementById(id)

    if (element) {
      element.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }

  render() {
    const { isDialogOpen, currentUser, currentEmployeeId, salesIncentiveUnit } = this.state
    const { activeStation, onDateChange } = this.props
    let { date } = this.props
    const units = salesIncentiveUnit ? UNITS[salesIncentiveUnit] : null
    const currentWorkingDay = getCurrentWorkingDay()

    // Show yesterday's card if chosen date is today but time is before 6:00 am (end of night shift)
    const showYesterdayAsCurrentWorkingDay = this.showYesterdayCardBeforeEndOfNightShift(date)
    date = showYesterdayAsCurrentWorkingDay || date

    // Yesterday's employee card can still be edited til 9:00am today
    const showSalesManagement = this.showSalesManagementBasedOnTime(date)

    const variables = {
      stationId: activeStation.id,
      date: date,
      mode: 'Day',
      status: 'InProgress',
    }

    const datePickerModifiers = {
      disabled: {
        after: new Date(currentWorkingDay),
      },
    }

    const hashLinks: HashLink[] = [
      { label: 'pageTop', id: '#top' },
      { label: 'incentiveSales', id: '#aloitteellinen-myynti' },
      { label: 'thumbableServices', id: '#peukutettavat-palvelut' },
      { label: 'hourlyChecklist', id: '#tarkistukset' },
      { label: 'dailyChecklist', id: '#johdettavat-tekemiset' },
    ]

    return (
      <div>
        <NoteDialog
          employee={currentUser}
          station={activeStation}
          isOpen={isDialogOpen}
          onClose={this.closeDialog}
          date={date}
        />
        <div className={css.dateSelector}>
          <div className={css.hashLinks}>
            {hashLinks.map((link, idx) => (
              <div key={idx} onClick={() => this.onNavigationClick(link.id)} className={css.hashLink}>
                {i18n.t(`employeeCard.hashLink.${link.label}`)}
                <ArrowRightIcon />
              </div>
            ))}
          </div>
          <DateBar
            date={date}
            onDateChange={onDateChange}
            scrollToBottom={true}
            className={css.dateBar}
            modifiers={datePickerModifiers}
            showRightArrow={moment(currentWorkingDay).isAfter(date)}
          />
        </div>
        <div className={css.selectContainer}>
          <Query query={getStaffingPlanWithAdditionalInfo} variables={variables} fetchPolicy='network-only'>
            {({ loading, error, data }) => (
              <Loader {...{ loading, error }}>
                <StaffMemberSelect
                  staffMembers={this.groupStaffPlanByEmployee({
                    assignments: getConnectionNodes(get(data, 'assignments', [])),
                    employeeShifts: get(data, 'staffPlan.roster', []),
                  })}
                  activityDescriptions={get(data, 'stationActivityDescriptions', [])}
                  label={i18n.t('employeeCard.currentEmployee')}
                  currentEmployeeId={currentEmployeeId}
                  employeeChanged={this.employeeChanged}
                  stationId={activeStation.id}
                  reservations={data.reservations}
                  date={date}
                />
              </Loader>
            )}
          </Query>
        </div>

        {currentUser.note === '' && (
          <div className={css.otherInformation}>
            <Button color='primary' variant='contained' className={css.button} onClick={() => this.openDialog()}>
              {i18n.t('employeeCard.addNote')}
            </Button>
          </div>
        )}

        {currentUser.note !== '' && (
          <div className={css.otherInformation}>
            <b className={css.otherInfoLabel}>{i18n.t('employeeCard.otherInfo')}</b>
            &nbsp;
            {currentUser.note}
            <span className={css.otherInfoButtons}>
              <EditButton onClick={() => this.openDialog()} />
            </span>
          </div>
        )}

        {currentEmployeeId && showSalesManagement && (
          <div className={css.buttonContainer}>
            <Query query={fetchDailyManagement} variables={variables}>
              {({ loading, error, data }) => (
                <Loader {...{ loading, error }}>
                  <div className={css.counterWrapper}>
                    <Counters dailyManagement={get(data, 'dailyManagement', {})} units={units} />
                  </div>
                  <SalesManagement
                    currentUserNote={currentUser.note}
                    setStationUnits={this.setStationUnits}
                    dailyManagement={get(data, 'dailyManagement', {})}
                    currentEmployeeId={currentEmployeeId}
                    stationId={activeStation.id}
                    date={date}
                  />
                </Loader>
              )}
            </Query>
          </div>
        )}
        <div className={css.Checklist}>
          <Paper className={css.checkListContainer}>
            <Checklist date={date} />
          </Paper>
        </div>
      </div>
    )
  }
}

export default IncentivesPanel
